import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { DepartmentCategory } from '../../../../../../../_base-shared/models/Department/DepartmentCategory';
import { User } from '../../../../../../../_base-shared/models/User/User';
import { MainGlobalEventService } from '../../../../_shared/services/main-global-event.service';
import { SharedModule } from '../../../../_shared/shared.module';
import { UserService } from '../../user.service';
import { LoaderComponent } from '../../../../../../../_base-shared/components/loader/loader.component';
import { AccomplishmentService } from '../../../config/system-configuration/accomplishment.service';
import { AccomplishmentTarget } from '../../../../../../../_base-shared/models/Accomplishment/AccomplishmentTarget';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector:    'app-target-user-editor',
  templateUrl: './target-user-editor.component.html',
  styles:      [],
  standalone:  true,
  imports:     [SharedModule, LoaderComponent],
})
export class TargetUserEditorComponent implements OnInit, OnDestroy {
  public authUser: User;
  public userId: number                                 = null;
  public user: User;
  public userDepartmentIds: number[]                    = [];
  public isLoading                                      = 0;
  public categoryDepartments: Array<DepartmentCategory> = [];
  public form: FormGroup;
  public formActive                            = false;
  public isSubmitting                          = false;
  private subscriptions: Array<Subscription>            = [];
  public currentLanguage: 'en' | 'es';

  constructor(private fb: FormBuilder,
              private cookieService: CookieService,
              private userService: UserService,
              private route: ActivatedRoute,
              private toastr: ToastrService,
              private translate: TranslateService,
              private globalEventsService: MainGlobalEventService,
              private accomplishmentService: AccomplishmentService) {
  }

  ngOnInit(): void {
    const storageLanguage = this.cookieService.get('lang');
    this.currentLanguage  = (storageLanguage === 'es' || storageLanguage === 'en') ? storageLanguage : 'es';
    this.subscriptions.push(
      this.translate.onLangChange.subscribe(next => {
        this.currentLanguage = (next.lang === 'es' || next.lang === 'en') ? next.lang : 'es';
      })
    );

    this.globalEventsService.authUser$.subscribe(user => {
      this.authUser = user;
      if (this.authUser) {
        this.userId = +this.route.parent.snapshot.paramMap.get('id');
        if (this.userId) {
          this.getUser(this.userId);
        } else {
          this.userId = null;
          this.user   = this.getUserDefaultData();
          this.fetchAccomplishmentTargets();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public departmentTargetsArray(): FormArray<any> {
    return this.form.get('department_targets') as FormArray;
  }

  public accomplishmentTargetsArray(index: number): any {
    return this.departmentTargetsArray().at(index).get('targets') as FormArray;
  }

  public submitForm(): void {
    console.log(this.form.value);
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    this.subscriptions.push(
      this.accomplishmentService.updateUserDepartmentTargets(this.userId, this.form.value)
        .pipe(finalize(() => this.isSubmitting = false))
        .subscribe({
          next:     () => {
            this.toastr.success(this.translate.instant('CONFIG.system-event.config-saved'));
          }, error: () => {
            this.toastr.error(this.translate.instant('SHARED.went_wrong'));
          }
        }),
    );
  }

  private fetchAccomplishmentTargets(): void {
    const requestData = {
      select_all:     1,
      department_ids: this.userDepartmentIds,
      per_individual: 1
    };

    this.isLoading++;
    this.subscriptions.push(
      this.accomplishmentService.indexUserDepartmentTargets(this.userId, requestData).pipe(finalize(() => this.isLoading--))
        .subscribe({
          next:  result => {
            this.categoryDepartments = result.data;
            this.buildForm(this.categoryDepartments);
          },
          error: error => console.error(error)
        }),
    );
  }

  private buildForm(categoryDepartments: DepartmentCategory[]): void {
    this.form                    = this.fb.group({
      department_targets: this.fb.array([]),
    });
    const departmentTargetsArray = this.form.get('department_targets') as FormArray;
    categoryDepartments.forEach(categoryDepartment => {
      if (categoryDepartment.departments?.length) {
        categoryDepartment.departments.forEach(department => {
          if (department?.accomplishment_targets?.length) {
            const departmentForm = this.fb.group({
              department: this.fb.group({
                id:      [department.id, Validators.required],
                name_en: [department.name_en, Validators.required],
                name_es: [department.name_es, Validators.required],
              }),
              targets:    this.fb.array([])
            });
            const targetsArray   = departmentForm.get('targets') as FormArray;
            department.accomplishment_targets.forEach(target => {
              if (target.is_active) {
                targetsArray.push(this.initTargetFormGroup(target));
              }
            });
            departmentTargetsArray.push(departmentForm);
          }
        });
      }
    });
    this.formActive = true;
  }

  private getUser(userId: number): void {
    this.isLoading++;
    this.userService.get(userId, {}, ['departments'])
      .pipe(finalize(() => this.isLoading--))
      .subscribe({
        next:  user => {
          this.user              = user.data;
          this.userDepartmentIds = this.user.departments.map(department => department.id);
          this.fetchAccomplishmentTargets();
        },
        error: err => console.error(err)
      });
  }

  private getUserDefaultData(): User {
    return new User();
  }

  private initTargetFormGroup(target: AccomplishmentTarget): FormGroup {
    return this.fb.group({
      id:      target.id,
      slug:    target.slug,
      name_en: target.name_en,
      name_es: target.name_es,
      goal:    target.agent_targets?.length ? target.agent_targets[0].goal : target.goal,
    });
  }
}
